/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ID, WithVersion } from '@gh/common-shared-data';
import { createEnum, EnumSet, WithOptimisticLock } from '@gh/core-data';
import { QuotationBase } from './quotation.dto';
import { ListingStatus, Storeys } from './hlp.mapper';

export const HlpStatus = createEnum('HLP_STATUS', {
  Draft: 1,
  Active: 2,
  Deleted: 3,
});
export type HlpStatus = EnumSet<typeof HlpStatus>;

export type HlpListItemDto = WithVersion<{
  id: ID;
  updatedDate: Date;
  hlpNumber: string;
  masterZoneName: string;
  officeName: string;
  officeId: ID;
  baseDesignName: string;
  quoteDescr: string;
  salespersonName: string;
  siteAddress: string;
  noOfLeads: number;
  floorArea: number;
  status: string;
  statusValue: HlpStatus;
  displayPrice: number;
  addedDate: Date;
  websiteLink: string;
}>;
export type HlpDetails = {
  houseAndLandPackageId: string;
  hlpNumber: string;
  housePlanId: string;
  externalId: string;
  base: QuotationBase;
  debit: number;
  credit: number;
  profit: number;
  status: HlpStatus;
  pbPrice: number;
  pbCost: number;
  version: number;
  totalPrice1: string;
  totalPrice2: string;
  priceBookId: string;
  priceBookName: string;
  housePlanCode: string;
  housePlanName: string;
  adjSalesTax: string;
  origSalesTax: string;
  pbSalesPrice: string;
  totalSalesTax: string;
  totalPriceWithGst: number;
  baseHousePlanId: string;
  baseHousePlanCode: string;
  baseHousePlanName: string;
  salesTaxRates: any[];
  variations: any[];
  quoteDescription: string;
  origSalesTaxPct: number;
  isRounded: boolean;
  isDisplayPriceOverridden: boolean;
  isSiteDetailsLocked: boolean;
  isSyncInProgress: boolean;
  description?: string;
  quoteHasChanges?: boolean;
  commonDetails: {
    alternateSalesperson?: { name: string; id: string };
    salesperson?: { name: string; id: string };
    displayPrice: string;
    isPublishedOnDomainComAu: boolean;
    isPublishedOnRealestateComAu: boolean;
    publishedById: number;
    publishedDate: string;
    publishedByName: string;
    listingStatus: ListingStatus;
    siteName: string;
    siteStreet: string;
    siteSuburb: string;
    sitePostcode: string;
    siteAddress: string;
    siteLotNumber: number;
    siteStreetNumber: number;
    siteUrl: string;
    latitude: number,
    longitude: number,
    memo: string;
  };
  designDetails: {
    designSeriesName: string;
    baseDesign?: { name: string; id: string };
    images: HlpDesignImage[];
    facades: HlpDesignImage[];
    floorPlans: HlpDesignImage[];
    websiteDescription: string;
    websiteDescriptionText?: string;
    bedrooms: number;
    bathrooms: number;
    carSpaces: number;
    floorArea: number;
    floorAreaUom: string;
    landArea: number;
    landAreaUom: string;
    storeys: Storeys;
    features: {id: ID; name: string; externalId?: number}[];
    additionalFeatures: string[];
  };
};

export type HlpDesignImage = {
  name: string;
  type: number;
  externalId?: string;
  hlpImageId?: string;
  isSelected: boolean;
  websiteLinks: {
    big: string;
    small: string;
    medium: string;
    original: string;
  };
};
export type HlpDetailsDto = WithOptimisticLock<WithVersion<{
  houseAndLandPackageId: string;
  hlpNumber: string;
  housePlanId: string;
  externalId: string;
  base: QuotationBase;
  debit: number;
  credit: number;
  profit: number;
  status: HlpStatus;
  pbPrice: number;
  pbCost: number;
  storeys: Storeys;
  version: number;
  bedrooms: number;
  bathrooms: number;
  carSpaces: number;
  floorArea: number;
  floorAreaUom: string;
  landArea: number;
  landAreaUom: string;
  images: HlpDesignImage[];
  totalPrice1: string;
  totalPrice2: string;
  totalPriceWithGst: number;
  priceBookId: string;
  priceBookName: string;
  housePlanCode: string;
  housePlanName: string;
  adjSalesTax: string;
  baseDesignId: string;
  origSalesTax: string;
  pbSalesPrice: string;
  totalSalesTax: string;
  baseDesignName: string;
  baseHousePlanId: string;
  designSeriesName: string;
  baseHousePlanCode: string;
  baseHousePlanName: string;
  features: {id: ID; name: string; externalId?: number}[];
  additionalFeatures: string[];
  salesTaxRates: any[];
  variations: any[];
  quoteDescription: string;
  origSalesTaxPct: number;
  isRounded: boolean;
  isSiteDetailsLocked: boolean;
  isSyncInProgress: boolean;
  baseDesign?: { name: string; id: string };
  description?: string;
  imageHasChanged?: boolean;
  additionalFeaturesHasChanged?: boolean;
  quoteHasChanges?: boolean;
  websiteDescription: string;
  websiteDescriptionText?: string;
  salespersonId: string;
  salespersonName: string;
  alternateSalespersonId: string;
  alternateSalespersonName: string;
  alternateSalesperson?: any;
  salesperson?: { name: string; id: string };
  isDisplayPriceOverridden: boolean;
  displayPrice: string;
  isPublishedOnDomainComAu: boolean;
  isPublishedOnRealestateComAu: boolean;
  publishedById: number;
  publishedDate: string;
  publishedByName: string;
  listingStatus: ListingStatus;
  siteName: string;
  siteStreet: string;
  siteSuburb: string;
  sitePostcode: string;
  siteAddress: string;
  siteLotNumber: number;
  siteStreetNumber: number;
  siteUrl: string;
  memo: string;
}>>;

export type HlpFeature = {
  externalId: number;
  id: ID;
  name: string;
};
